import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthCtx } from "../lib/auth-provider";
import { Brand } from "../components/brand";
import { LangToggle } from "../components/lang-toggle";
import { useProfileStatus } from "../lib/profile";
import { profileGateRedirect, profileGateWaits } from "../lib/profile-gate";

const HomeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 12 L 12 4 L 21 12" />
    <path d="M5 11 L 5 20 L 19 20 L 19 11" />
    <path d="M10 20 L 10 14 L 14 14 L 14 20" />
  </svg>
);
const AccountIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21 C 4 16, 8 14, 12 14 C 16 14, 20 16, 20 21" />
  </svg>
);
const AdminIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3 L 4 7 V 12 C 4 16, 7 20, 12 21 C 17 20, 20 16, 20 12 V 7 Z" />
    <path d="M9 12 L 11 14 L 15 10" />
  </svg>
);
const InboxIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 13 L 7 5 H 17 L 20 13" />
    <path d="M4 13 V 19 H 20 V 13 H 15 L 14 15 H 10 L 9 13 Z" />
  </svg>
);
const SignInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M15 3 H 20 V 21 H 15" />
    <path d="M10 8 L 14 12 L 10 16" />
    <path d="M4 12 H 14" />
  </svg>
);

export function Layout() {
  const { t } = useTranslation();
  const { user, claims } = useAuthCtx();
  const isAdmin = (claims as { admin?: boolean } | null)?.admin === true;
  const location = useLocation();
  const isAuthRoute = location.pathname === "/login" || location.pathname === "/signup";
  // Accounts without company and phone are held on /complete-profile until
  // they fill them in — on every page, not only right after logging in, since
  // a remembered session lands on /home without going through /login.
  const profileStatus = useProfileStatus(user?.uid ?? null);
  const gateTo = profileGateRedirect(location.pathname, location.search, profileStatus);
  if (gateTo) return <Navigate to={gateTo} replace />;

  if (isAuthRoute) {
    // Auth pages render their own full-viewport split-screen shell.
    return <Outlet />;
  }

  return (
    <div className="acx__app">
      <header className="acx__topbar">
        <Link to="/" style={{ display: "inline-flex" }}><Brand size="md" /></Link>
        <nav className="acx__topbar-nav">
          {user ? (
            <>
              <NavLink to="/home" className="acx__topbar-link" aria-label={t("nav.home")}>
                <HomeIcon />
                <span className="acx__topbar-link-label">{t("nav.home")}</span>
              </NavLink>
              <NavLink to="/account" className="acx__topbar-link" aria-label={t("nav.account")}>
                <AccountIcon />
                <span className="acx__topbar-link-label">{t("nav.account")}</span>
              </NavLink>
              {isAdmin && (
                <NavLink to="/admin" className="acx__topbar-link" aria-label={t("nav.admin")}>
                  <AdminIcon />
                  <span className="acx__topbar-link-label">{t("nav.admin")}</span>
                </NavLink>
              )}
              {isAdmin && (
                <NavLink to="/admin/requests" className="acx__topbar-link" aria-label={t("nav.requests")}>
                  <InboxIcon />
                  <span className="acx__topbar-link-label">{t("nav.requests")}</span>
                </NavLink>
              )}
            </>
          ) : (
            <NavLink to="/login" className="acx__topbar-link" aria-label={t("nav.signIn")}>
              <SignInIcon />
              <span className="acx__topbar-link-label">{t("nav.signIn")}</span>
            </NavLink>
          )}
          <LangToggle />
        </nav>
      </header>
      <main className="acx__main">{profileGateWaits(location.pathname, profileStatus) ? null : <Outlet />}</main>
    </div>
  );
}
